// E12-t29/t30 (web half) — THE APP'S HALF OF THE LOGIN, as a transport.
//
// Contract: git-host/src/login.ts — POST /api/auth/{signup,signin,signout}. Its
//   header states the two facts this file is written against: the token is in an
//   HttpOnly cookie AND NOWHERE ELSE (so nothing here reads or stores a token),
//   and every credential refusal is ONE answer byte for byte
//   (`invalid_credentials`, "invalid email or password"), so this file must not
//   invent a second dialect by guessing which field was wrong.
// Contract: git-host/src/auth.ts — GET /api/auth/device/pending and POST
//   /api/auth/device/approve, the read and the write behind /auth/device. Its
//   header states that pending NEVER carries the device half, and that an unknown
//   code and a spent one answer alike so the page cannot become a code oracle.
// Contract: vault/Flows/Tenancy and Guards.md f5 (device-code flow for the CLI,
//   accounts as identity) and s2 (a refusal is an ordinary answer WITH THE GUARD
//   NAMED — so a 403 travels up in the evaluator's own words, never flattened).
// Contract: vault/Backlog/E12 Testable Surface and Authoring.md t29, t30.
//
// SHAPED LIKE src/api/dashboards.ts, deliberately: the pages take a CLIENT and
// never touch global fetch, which is what lets their component contracts run with
// no network, no msw and no backend. The one difference from that file is the
// default `fetchImpl`, which is a thunk (`(input, init) => fetch(input, init)`)
// rather than the `fetch` binding itself: this transport is constructed at module
// load in App.tsx, and a captured binding could never be replaced by a test that
// renders the whole app. Late binding costs one closure and buys the route test.
//
// THE SESSION IS PARSED IN EXACTLY ONE PLACE. Sign-up and sign-in answer
// `sessionProjection` — the same value GET /api/session answers with, extracted in
// git-host/src/catalog.ts precisely so the two surfaces cannot disagree about who
// is signed in — so this file calls src/session.ts's parser rather than growing a
// second reader of that shape.
import type { SessionPayload } from '../shell/AppShell';
import { parseSessionPayload } from '../session';

/** git-host/src/login.ts — the three interactive routes, spelled once. */
export const SIGNUP_ENDPOINT = '/api/auth/signup';
export const SIGNIN_ENDPOINT = '/api/auth/signin';
export const SIGNOUT_ENDPOINT = '/api/auth/signout';
/** git-host/src/auth.ts — the approval page's read, and the write it precedes. */
export const DEVICE_PENDING_ENDPOINT = '/api/auth/device/pending';
export const DEVICE_APPROVE_ENDPOINT = '/api/auth/device/approve';

/**
 * A refusal the control plane made, carried up in ITS OWN WORDS.
 *
 * `code` is the machine word the Worker sent (`invalid_credentials`,
 * `invalid_grant`, `expired_token`, `forbidden`, `unauthenticated`), because the
 * page's copy depends on WHICH refusal it was — "that code expired, run `djc auth
 * login` again" is a different sentence from "that code is not one we can
 * approve". `guard` is present only on a 403 and is the guard the shared evaluator
 * named (platform/src/guards.ts); Tenancy s2 is why it is not thrown away.
 */
export class AuthRefusal extends Error {
  readonly code: string;
  readonly status: number;
  readonly guard: string | null;

  constructor(code: string, message: string, status: number, guard: string | null = null) {
    super(message);
    this.name = 'AuthRefusal';
    this.code = code;
    this.status = status;
    this.guard = guard;
  }
}

/** GET /api/auth/device/pending, in the app's own words. Never a device code:
 *  the route does not carry one, and this type has nowhere to put one. */
export interface DevicePending {
  /** the code AS IT WAS PRINTED — never re-cased or de-hyphenated (auth.ts) */
  userCode: string;
  /** what the client CALLED ITSELF. A claim, not an identity — the page says so. */
  clientId: string;
  /** the org this login was asked FOR, or null when none was named */
  org: string | null;
  /** whole seconds of remaining life */
  expiresInSeconds: number;
  /** platform/src/device-code.ts's own word for the state */
  status: 'pending' | 'approved' | 'redeemed' | 'denied';
}

export interface Credentials {
  email: string;
  password: string;
}

export interface SignUpInput extends Credentials {
  /** only the PERSONAL ORG's name is derived from it (platform/src/accounts.ts) */
  displayName?: string;
}

/** The five questions the pages ask. Injected, so no page holds a fetch. */
export interface AuthClient {
  signIn(input: Credentials): Promise<SessionPayload>;
  signUp(input: SignUpInput): Promise<SessionPayload>;
  signOut(): Promise<void>;
  pendingDevice(userCode: string): Promise<DevicePending>;
  approveDevice(userCode: string): Promise<void>;
}

const str = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const STATUSES: ReadonlyArray<DevicePending['status']> = [
  'pending',
  'approved',
  'redeemed',
  'denied',
];

/**
 * The ONE place a non-ok answer becomes an AuthRefusal.
 *
 * A body that is not JSON, or that names no error, still refuses — with the status
 * as the code — because a page that renders nothing when the backend answers 502 is
 * a page that lies about what happened. The MESSAGE is the server's when there is
 * one: git-host/src/login.ts owns the wording of a credential refusal, and
 * rewriting it here is how two surfaces start disagreeing about the same "no".
 */
async function refuse(response: Response, fallback: string): Promise<AuthRefusal> {
  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
    message?: string;
    reason?: string;
    guard?: string;
  };
  const code = str(body.error) ?? `http_${response.status}`;
  const message = str(body.message) ?? str(body.reason) ?? fallback;
  return new AuthRefusal(code, message, response.status, str(body.guard));
}

/** A POST of one JSON object, with the cookie, on the app's own origin. */
async function post(
  fetchImpl: typeof fetch,
  path: string,
  body: Record<string, unknown>,
): Promise<Response> {
  return await fetchImpl(path, {
    method: 'POST',
    // same-origin: the session travels as the HttpOnly `dj_session` cookie
    // (platform/src/session-store.ts) and the app and the Worker are ONE origin
    // in the browser — see web/vite.config.ts.
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * The projection, read once. A 2xx whose body is not a session is a bug in the
 * Worker and is refused as one, rather than becoming a signed-in-looking app with
 * no identity in it.
 */
async function sessionFrom(response: Response): Promise<SessionPayload> {
  const body = (await response.json().catch(() => null)) as unknown;
  const session = parseSessionPayload(body);
  if (session === null) {
    throw new AuthRefusal(
      'malformed_session',
      'the sign-in succeeded but the control plane did not answer with an identity',
      response.status,
    );
  }
  return session;
}

export function createAuthClient(
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): AuthClient {
  return {
    async signIn(input: Credentials): Promise<SessionPayload> {
      const response = await post(fetchImpl, SIGNIN_ENDPOINT, {
        email: input.email,
        password: input.password,
      });
      if (!response.ok) throw await refuse(response, 'invalid email or password');
      return await sessionFrom(response);
    },

    async signUp(input: SignUpInput): Promise<SessionPayload> {
      const response = await post(fetchImpl, SIGNUP_ENDPOINT, {
        email: input.email,
        password: input.password,
        // absent rather than empty: accounts.ts falls back to the address, and a
        // blank string is not the same request as an unstated one
        ...(input.displayName ? { display_name: input.displayName } : {}),
      });
      if (!response.ok) throw await refuse(response, 'invalid email or password');
      return await sessionFrom(response);
    },

    async signOut(): Promise<void> {
      const response = await post(fetchImpl, SIGNOUT_ENDPOINT, {});
      // A 401 here means the session was already gone, which is the outcome the
      // caller asked for: it is not an error to sign out twice.
      if (!response.ok && response.status !== 401) {
        throw await refuse(response, 'the sign-out was refused');
      }
    },

    async pendingDevice(userCode: string): Promise<DevicePending> {
      const path = `${DEVICE_PENDING_ENDPOINT}?user_code=${encodeURIComponent(userCode)}`;
      const response = await fetchImpl(path, { credentials: 'same-origin' });
      if (!response.ok) throw await refuse(response, 'that code cannot be approved');
      const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      const status = body.status as DevicePending['status'];
      return {
        userCode: str(body.user_code) ?? userCode,
        // an unnamed client is recorded as "unknown" by the mint; it is shown as
        // the mint recorded it rather than dressed up
        clientId: str(body.client_id) ?? 'unknown',
        org: str(body.org),
        expiresInSeconds:
          typeof body.expires_in === 'number' && Number.isFinite(body.expires_in)
            ? Math.max(0, Math.floor(body.expires_in))
            : 0,
        status: STATUSES.includes(status) ? status : 'pending',
      };
    },

    async approveDevice(userCode: string): Promise<void> {
      const response = await post(fetchImpl, DEVICE_APPROVE_ENDPOINT, { user_code: userCode });
      if (!response.ok) throw await refuse(response, 'that code cannot be approved');
    },
  };
}
